/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route } from 'react-router-dom';
import { compose } from 'recompose';
import { func, shape, string } from 'prop-types';

import Helper from '../state/reducers/Helper';
import { withAuth } from '../../../../state/ducks/Member/ducks/Auth/Plugin/auth';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import mbpUtil from 'mbp-api-util';
import { LOGIN_URL } from '@auth/mbp-next/app/react';
import { getTokenLocalStorage } from '../../../../state/ducks/Member/ducks/Auth/helper/helper';

const MAX_RETRIES = 5;
const RETRY_DELAY = 300;


const REACT_APP_INJECTABLE = mbpUtil.getEnv('REACT_APP_INJECTABLE');

/* eslint-disable react/jsx-props-no-spreading */
const ProtectedRoute = ({ component: Component, auth, path, ...rest }) => {
    const isAccountAuthCheckRetryOn = useSelector(getFeatureFlag(['is-account-auth-check-retry-on'])) && REACT_APP_INJECTABLE !== 'false';

    if (isAccountAuthCheckRetryOn) {
        const [authState, setAuthState] = useState({
            isAuthenticated: null,
            retries: 0,
            loading: true
        });

        useEffect(() => {
            let retryTimeout; // Store the timeout ID so we can clear it if needed

            // Check authentication status
            const checkAuth = async () => {
                try {
                    // Get authentication details
                    const authentication = await Helper.getAuthentication();
                    console.log(`[+] Attempt ${authState.retries + 1} of ${MAX_RETRIES} - isAuthenticated:`, authentication.isAuthenticated);

                    if (authentication.isAuthenticated) {
                        // User is authenticated, update state and stop retries
                        setAuthState({
                            isAuthenticated: true,
                            retries: authState.retries,
                            loading: false
                        });
                    } else if (authState.retries < MAX_RETRIES) {
                        // User is not authenticated, retry after a delay
                        retryTimeout = setTimeout(() => {
                            setAuthState(prev => ({
                                ...prev,
                                retries: prev.retries + 1 // Increment retry count
                            }));
                        }, RETRY_DELAY);
                    } else {
                        // Max retries reached, stop checking and mark user as unauthenticated
                        console.log('[+] MAX RETRIES REACHED - User is NOT authenticated');
                        setAuthState({
                            isAuthenticated: false,
                            retries: MAX_RETRIES,
                            loading: false
                        });
                    }
                } catch (error) {
                    // Console any errors that occur during authentication check
                    console.error('Error during authentication check:', error);
                    setAuthState({
                        isAuthenticated: false,
                        retries: MAX_RETRIES,
                        loading: false
                    });
                }
            };

            // Start auth check
            checkAuth();

            return () => {
                // Clean up timeout if component unmounts
                if (retryTimeout) clearTimeout(retryTimeout);
            };
        }, [authState.retries]);

        useEffect(() => {
            if (authState.isAuthenticated === false && authState.loading === false) {
                console.log('[+] REDIRECTING to login...');
                auth.loginByRedirect({}, { routeBack: path });
            }
        }, [authState.isAuthenticated, authState.loading, auth, path]);

        if (authState.loading) {
            return <div>Loading...</div>;
        }
        return authState.isAuthenticated ? <Route {...rest} exact render={(props) => <Component {...props} />} /> : null;
    }

    const { isAuthenticated:isAuthUser } = Helper.getAuthentication();

    const [isAuthenticated, setIsAuthenticated] = useState(isAuthUser || false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isAuthenticatedSession = false;
        if (REACT_APP_INJECTABLE === 'false') {
            const localStorageUserInfo = getTokenLocalStorage('userInfo');
            if (localStorageUserInfo) {
                const userInfo = JSON.parse(localStorageUserInfo);
                isAuthenticatedSession = (userInfo?.profile?.membershipType === 'R' || userInfo?.profile?.membershipType === 'P' || userInfo?.profile?.ContactId)
            }
        } else {
            isAuthenticatedSession = isAuthUser
        }
        setIsAuthenticated(isAuthenticatedSession);
        setLoading(false);
    }, [1]);

    if (loading) {
        return <></>
    }



    return (
        <Route
            {...rest}
            exact
            render={(props) => {
                if (!isAuthenticated) {
                    if (REACT_APP_INJECTABLE === 'false') {
                        window.location.href = `${LOGIN_URL}?returnTo=${encodeURIComponent('/z/account/account?loggedIn=1')}`
                    } else {
                        auth.loginByRedirect({}, { routeBack: path });
                    }
                }
                return <Component {...props} />;
            }}
        />
    )
};

ProtectedRoute.propTypes = {
    component: func.isRequired,
    auth: shape({
        loginByRedirect: func.isRequired,
    }).isRequired,
    path: string.isRequired,
};

const enhance = compose(withAuth);
export default enhance(ProtectedRoute);
